import { useCallback, useEffect, useState } from 'react';

import { useAppKitAccount, useAppKitNetworkCore } from '@reown/appkit/react';
import { Contract, ethers } from 'ethers';
import { toast } from 'sonner';

import { web3Config } from '@/configs/web3-config';
import { useWeb3WalletStore } from '@/store';
import { useWallet } from '@/lib/hooks/web3/useWallet';

export const useFetchWalletBalance = () => {
  const [balanceLoading, setBalanceLoading] = useState(false);
  const { setTokenBalance } = useWeb3WalletStore();
  const { ethersProvider, wrongNetwork } = useWallet();
  const { isConnected, address } = useAppKitAccount();
  const { chainId } = useAppKitNetworkCore();

  const fetchTokenBalance = useCallback(
    async (tokenConfig) => {
      if (!ethersProvider || !isConnected || !address || wrongNetwork) {
        return null;
      }

      try {
        setBalanceLoading(true);
        
        const chainIdStr = chainId.toString();
        let contractAddress = tokenConfig.chains[chainIdStr];
        
        if (!contractAddress) {
          return "0.0";
        }
        
        const signer = await ethersProvider.getSigner();
        const contract = new Contract(
          contractAddress,
          tokenConfig.ABI,
          signer
        );
        
        const tokenBalance = await contract.balanceOf(address);
        const formattedBalance = ethers.formatEther(tokenBalance.toString());

        return formattedBalance;
      } catch (error) {
        toast.error(
          `Error fetching ${tokenConfig.symbol} balance: ${error?.shortMessage || error.message}`
        );
        return "0.0";
      } finally {
        setBalanceLoading(false);
      }
    },
    [ethersProvider, isConnected, address, wrongNetwork, chainId]
  );

  const fetchTwinTokenBalance = useCallback(async () => {
    const chainIdStr = chainId.toString();
    const isSupported = Object.keys(web3Config.TWIN.chains).includes(chainIdStr);
    
    if (!isSupported) {
      setTokenBalance("0.0");
      return;
    }
    
    const twinBalance = await fetchTokenBalance(web3Config.TWIN);
    if (twinBalance) setTokenBalance(twinBalance);
  }, [fetchTokenBalance, setTokenBalance, chainId]);

  useEffect(() => {
    if (!ethersProvider || !isConnected || !address) {
      return;
    }
    
    fetchTwinTokenBalance();
    
    const intervalId = setInterval(() => {
      fetchTwinTokenBalance();
    }, 15000); // Check every 15 seconds
    
    return () => clearInterval(intervalId);
  }, [fetchTwinTokenBalance, ethersProvider, isConnected, address]);

  return { fetchTwinTokenBalance, balanceLoading };
};